/**
 * The study's own interventions: the analysis's list minus any product the
 * protocol only MENTIONS (a device an earlier study used, a drug from the
 * background literature) and never also uses as a study product.
 *
 * The website's copy of `studyInterventions` in
 * functions/src/studyProfile/studyProducts.ts. The two are run on the same
 * examples by studyInterventions.test.ts, so a change to one that is not made to
 * the other fails the suite. An analysis made before 2026-10-27 has no
 * `studyProducts` and keeps every intervention.
 */
type Rec = Record<string, unknown>;
const obj = (v: unknown): Rec => (v && typeof v === 'object' && !Array.isArray(v) ? (v as Rec) : {});
const str = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');

function norm(s: string): string {
  return s
    .replace(/[™®©]/g, '')
    .replace(/[‐-―]/g, '-')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

/** Words that say what KIND of thing a product is, not which one. */
const GENERIC = new Set([
  'a', 'an', 'and', 'the', 'of', 'with', 'for', 'in', 'on', 'or',
  'system', 'systems', 'device', 'devices', 'kit', 'kits', 'model',
  'tablet', 'tablets', 'capsule', 'capsules', 'injection', 'solution', 'oral',
  'mg', 'mcg', 'ml', 'dose', 'therapy', 'treatment', 'product',
]);

function distinctWords(s: string): Set<string> {
  return new Set(norm(s).replace(/[^\p{L}\p{N} ]/gu, ' ').split(/\s+/).filter((x) => x && !GENERIC.has(x)));
}

/** Shared distinctive words and the share of the product's name they cover; null when not a match. */
function closeness(intervention: Set<string>, product: string): { shared: number; coverage: number } | null {
  const pw = distinctWords(product);
  if (!pw.size) return null;
  let shared = 0;
  for (const w of pw) if (intervention.has(w)) shared++;
  const coverage = shared / pw.size;
  if (!shared || coverage < 0.5 || (shared === 1 && coverage < 1)) return null;
  return { shared, coverage };
}

/**
 * Each intervention is matched to the product it names most closely (most
 * shared words, then the larger share of that product's name) and left out when
 * that product is mentioned only and no product the study uses matches as closely.
 */
export function studyInterventions<T = Rec>(analysis: { interventions?: unknown; studyProducts?: unknown } | null | undefined): T[] {
  const list = Array.isArray(analysis?.interventions) ? (analysis!.interventions as T[]) : [];
  const products = Array.isArray(obj(analysis?.studyProducts).products) ? (obj(analysis?.studyProducts).products as Rec[]) : [];
  if (!products.length) return list;
  return list.filter((iv) => {
    const name = typeof iv === 'string' ? iv.trim() : str(obj(iv).name);
    if (!name) return true;
    const words = distinctWords(name);
    let best: { shared: number; coverage: number; mentioned: boolean; used: boolean } | null = null;
    for (const p of products) {
      const c = closeness(words, str(p?.name));
      if (!c) continue;
      const mentioned = p.role === 'mentioned_only';
      if (!best || c.shared > best.shared || (c.shared === best.shared && c.coverage > best.coverage)) {
        best = { ...c, mentioned, used: !mentioned };
      } else if (c.shared === best.shared && c.coverage === best.coverage) {
        if (mentioned) best.mentioned = true;
        else best.used = true;
      }
    }
    return !best || !best.mentioned || best.used;
  });
}
